/**
 * vf-facets.js — colour families on top of facets.js (which stays untouched).
 *
 * facets.liquid renders one checkbox per colour family (no name, never submitted) plus a hidden real checkbox for every raw
 * Color value (data-vf-raw, data-vf-families=",beige,white,"). Here we keep the two in step and tell the facet form once:
 *   - ticking a family ticks every pickable raw value of that family; unticking clears them, except values that belong to
 *     another family that is still ticked (a "Beige - White" value stays while Beige or White is selected);
 *   - the form's own `change` listener (FacetForm.onFormChange) then builds the URL from the raw checkboxes as usual;
 *   - the family chips above the grid (data-vf-family-remove) untick their family through the same path;
 *   - the "(N)" counters (drawer apply button, filter button) count a family once instead of once per raw value.
 * The Size filter uses the same hidden raw checkboxes behind three range sliders (length / width / height), see "Size sliders".
 * No direction-specific code, so it is RTL-safe.
 */
(() => {
  if (window.__vfFacets) return;
  window.__vfFacets = true;

  const has = (raw, key) => raw.dataset.vfFamilies.includes(`,${key},`);

  /** Untick/tick the raw values of one family, then refresh the family controls of the same group. */
  function applyFamily(ctrl) {
    const box = ctrl.closest('[data-vf-colors]');
    if (!box) return;
    const raws = [...box.querySelectorAll('input[data-vf-raw]')];
    const ctrls = [...box.querySelectorAll('input[data-vf-family]')];
    const key = ctrl.dataset.vfFamily;

    for (const raw of raws) {
      if (!has(raw, key)) continue;
      if (ctrl.checked) {
        if (!raw.disabled) raw.checked = true;
      } else {
        raw.checked = ctrls.some((c) => c !== ctrl && c.checked && has(raw, c.dataset.vfFamily));
      }
    }
    syncFamilies(box);
  }

  /** A family looks ticked when every pickable value of it is ticked, "partial" when only some are. */
  function syncFamilies(box) {
    const raws = [...box.querySelectorAll('input[data-vf-raw]')];
    for (const ctrl of box.querySelectorAll('input[data-vf-family]')) {
      const own = raws.filter((r) => has(r, ctrl.dataset.vfFamily) && !r.disabled);
      const on = own.filter((r) => r.checked).length;
      ctrl.checked = own.length > 0 && on === own.length;
      ctrl.closest('.vf-family')?.setAttribute('data-vf-state', ctrl.checked ? '2' : on ? '1' : '0');
    }
  }

  // Capture phase: runs before the form's own listener, so the form hears one `change` after the raw values are set.
  document.addEventListener(
    'change',
    (event) => {
      const ctrl = event.target;
      if (!(ctrl instanceof HTMLInputElement) || !ctrl.hasAttribute('data-vf-family')) return;
      event.stopPropagation();
      applyFamily(ctrl);
      const form = ctrl.form || ctrl.closest('form');
      if (form) form.dispatchEvent(new Event('change', { bubbles: true }));
    },
    true
  );

  // Family chip "x": untick that family in whichever filter form has it.
  document.addEventListener(
    'click',
    (event) => {
      const chip = event.target instanceof Element ? event.target.closest('[data-vf-family-remove]') : null;
      if (!chip) return;
      const ctrl = document.querySelector(
        `[data-vf-kind="${chip.dataset.vfKind}"] input[data-vf-family="${chip.dataset.vfFamilyRemove}"]`
      );
      if (!ctrl) return; // no control on the page: the link's own href (one value) still works
      event.preventDefault();
      event.stopPropagation();
      ctrl.checked = false;
      ctrl.dispatchEvent(new Event('change', { bubbles: true }));
    },
    true
  );

  // Price shortcut "under N": clear the lower price, put N in the upper price box and fire the change the box fires itself
  // (PriceRange syncs its slider, the form then submits once), so the URL carries filter.v.price.lte=N and nothing else changes.
  document.addEventListener('click', (event) => {
    const chip = event.target instanceof Element ? event.target.closest('[data-vf-price-max]') : null;
    if (!chip || chip.getAttribute('aria-pressed') === 'true') return;
    const form = chip.closest('form');
    const max = form?.querySelector('input[name="filter.v.price.lte"]');
    const min = form?.querySelector('input[name="filter.v.price.gte"]');
    if (!max) return;
    event.preventDefault();
    if (min) min.value = '';
    max.value = chip.dataset.vfPriceMax;
    max.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /*
   * Size sliders (snippets/vf-size-dims.liquid): length / width / height ranges over the raw Size values of the collection.
   * Each raw value is parsed once ("200.0L - 90.0W - 80.0H", "160cm L - 80cm W", "Width 70cm-Depth 80cm-Height 95cm"; D counts
   * as width when there is no W). On release the raw values that fit every narrowed slider are ticked and the form hears one
   * `change`; a value without a narrowed dimension is left out. The chosen ranges are kept in sessionStorage per page, keyed by
   * the ticked values, so the handles come back where the shopper left them after the grid re-renders.
   */
  const DIMS = ['l', 'w', 'h'];
  const span = (a, b) => `⁦${a}–${b}⁩`; // LTR isolate: an en dash between numbers flips to "240–150" in Arabic
  const dimsKey = () => `vf-dims:${location.pathname}`;

  function parseDims(value) {
    const s = value
      .toLowerCase()
      .replace(/(width|length|depth|height)\s*(\d+(?:\.\d+)?)\s*cm/g, (m, k, n) => n + k[0]);
    const d = {};
    for (const m of s.matchAll(/(\d+(?:\.\d+)*)\s*(?:cm)?\s*([lwhd])(?![a-z])/g)) {
      const n = parseFloat(m[1]);
      if (n > 0 && d[m[2]] === undefined) d[m[2]] = n;
    }
    if (d.w === undefined && d.d !== undefined) d.w = d.d;
    return d;
  }

  const signature = (raws) =>
    raws
      .filter((r) => r.checked)
      .map((r) => r.value)
      .sort()
      .join('|');

  function readStored(sig) {
    try {
      const saved = JSON.parse(sessionStorage.getItem(dimsKey()) || 'null');
      return saved && saved.sig === sig ? saved.ranges : null;
    } catch (e) {
      return null;
    }
  }

  function rangeText(box, ranges) {
    const unit = box.dataset.vfUnit;
    return DIMS.filter((k) => ranges[k])
      .map((k) => `${box.dataset[`vfLabel${k.toUpperCase()}`]} ${span(ranges[k][0], ranges[k][1])} ${unit}`)
      .join(' · ');
  }

  function initDims(box) {
    if (box.vfDims) return;
    const raws = [...box.querySelectorAll('input[data-vf-raw]')];
    const items = raws.map((raw) => ({ raw, d: parseDims(raw.value) }));
    const rows = {};

    for (const k of DIMS) {
      const row = box.querySelector(`[data-vf-dim="${k}"]`);
      const nums = items.map((it) => it.d[k]).filter((n) => n !== undefined);
      if (!row || nums.length < 2) continue;
      const lo = Math.floor(Math.min(...nums));
      const hi = Math.ceil(Math.max(...nums));
      if (hi <= lo) continue;
      const min = row.querySelector('[data-vf-dim-min]');
      const max = row.querySelector('[data-vf-dim-max]');
      for (const input of [min, max]) {
        input.min = lo;
        input.max = hi;
        input.step = 1;
      }
      rows[k] = { row, min, max, lo, hi, out: row.querySelector('output'), wrap: min.parentElement };
      row.hidden = false;
    }
    if (!Object.keys(rows).length) return;
    box.vfDims = { items, rows };

    // Handles: the stored ranges when they belong to the ticked values, else the spread of the ticked values, else full.
    const ticked = items.filter((it) => it.raw.checked);
    const stored = ticked.length ? readStored(signature(raws)) : null;
    for (const [k, r] of Object.entries(rows)) {
      let a = r.lo;
      let b = r.hi;
      if (stored?.[k]) {
        [a, b] = stored[k];
      } else if (ticked.length && !stored) {
        const nums = ticked.map((it) => it.d[k]).filter((n) => n !== undefined);
        if (nums.length) {
          a = Math.floor(Math.min(...nums));
          b = Math.ceil(Math.max(...nums));
        }
      }
      r.min.value = Math.max(r.lo, Math.min(a, r.hi));
      r.max.value = Math.min(r.hi, Math.max(b, r.lo));
      paintDim(box, r);
    }
  }

  function paintDim(box, r) {
    const a = +r.min.value;
    const b = +r.max.value;
    const width = r.hi - r.lo;
    r.wrap.style.setProperty('--range-min', `${((a - r.lo) / width) * 100}%`);
    r.wrap.style.setProperty('--range-max', `${((b - r.lo) / width) * 100}%`);
    r.out.textContent = `${span(a, b)} ${box.dataset.vfUnit}`;
  }

  /** Narrowed sliders → ranges object ({l: [150, 240]}); full-width sliders are left out. */
  function chosenRanges(box) {
    const ranges = {};
    for (const [k, r] of Object.entries(box.vfDims.rows)) {
      const a = +r.min.value;
      const b = +r.max.value;
      if (a > r.lo || b < r.hi) ranges[k] = [a, b];
    }
    return ranges;
  }

  function applyDims(box) {
    const { items } = box.vfDims;
    const ranges = chosenRanges(box);
    const keys = Object.keys(ranges);
    const fits = (it) =>
      !it.raw.disabled && keys.every((k) => it.d[k] !== undefined && it.d[k] >= ranges[k][0] && it.d[k] <= ranges[k][1]);
    const picked = keys.length ? items.filter(fits) : [];
    const empty = box.querySelector('.vf-dims__empty');

    if (keys.length && !picked.length) {
      empty.hidden = false; // nothing fits: keep the current results and let the shopper widen a slider
      return;
    }
    empty.hidden = true;
    for (const it of items) it.raw.checked = picked.includes(it);
    try {
      sessionStorage.setItem(dimsKey(), JSON.stringify({ sig: signature(items.map((it) => it.raw)), ranges }));
    } catch (e) {}
    const form = box.closest('form');
    if (form) form.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // Dragging: keep the two handles apart and repaint; releasing: apply (capture phase, so facets.js never sees the slider).
  document.addEventListener('input', (event) => {
    const input = event.target;
    const box = input instanceof HTMLInputElement ? input.closest('[data-vf-dims]') : null;
    if (!box?.vfDims || input.type !== 'range') return;
    const r = box.vfDims.rows[input.closest('[data-vf-dim]').dataset.vfDim];
    if (input === r.min) r.min.value = Math.min(+r.min.value, +r.max.value);
    else r.max.value = Math.max(+r.max.value, +r.min.value);
    paintDim(box, r);
  });
  document.addEventListener(
    'change',
    (event) => {
      const input = event.target;
      const box = input instanceof HTMLInputElement && input.type === 'range' ? input.closest('[data-vf-dims]') : null;
      if (!box) return;
      event.stopPropagation();
      if (box.vfDims) applyDims(box);
    },
    true
  );

  /** Chip above the grid: "الطول 150–240 سم · العرض 60–90 سم" when the stored ranges match the ticked values. */
  function labelDimsChip() {
    const chip = document.querySelector('[data-vf-dims-chip]');
    const box = document.querySelector('[data-vf-dims]');
    if (!chip || !box) return;
    const ranges = readStored(signature([...box.querySelectorAll('input[data-vf-raw]')]));
    if (ranges && Object.keys(ranges).length) chip.textContent = rangeText(box, ranges);
  }

  function initAllDims() {
    document.querySelectorAll('[data-vf-dims]').forEach(initDims);
    labelDimsChip();
  }

  /** Number of active filters with a family counted once: price, plain values, families, and values no family covers. */
  function activeCount(form) {
    let n = 0;
    const price = ['filter.v.price.gte', 'filter.v.price.lte'].some((name) => form.querySelector(`input[name="${name}"]`)?.value);
    if (price) n++;
    n += [...form.querySelectorAll('[data-vf-dims]')].filter((box) => box.querySelector('input[data-vf-raw]:checked')).length;
    n += form.querySelectorAll('input[type="checkbox"][name]:checked:not([data-vf-raw])').length;
    for (const box of form.querySelectorAll('[data-vf-colors]')) {
      const ctrls = [...box.querySelectorAll('input[data-vf-family]')];
      n += ctrls.filter((c) => c.checked).length;
      n += [...box.querySelectorAll('input[data-vf-raw]:checked')].filter(
        (r) => !ctrls.some((c) => c.checked && has(r, c.dataset.vfFamily))
      ).length;
    }
    return n;
  }

  function updateCounters() {
    const form =
      document.querySelector('#DrawerFacetFiltersContainer form[is="facet-form"]') ||
      document.querySelector('form[is="facet-form"]');
    if (!form) return;
    const n = activeCount(form);
    document.querySelectorAll('facet-count').forEach((el) => {
      el.textContent = `(${n})`;
      el.hidden = n === 0;
    });
  }

  // facets.js sets the counters from the server (raw values) and then fires this event; we correct them afterwards.
  const refresh = () => {
    initAllDims();
    updateCounters();
  };
  document.addEventListener('collection:rerendered', refresh);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh);
  else refresh();
})();
